import axios from 'axios';
import { auth } from '../config/firebase';
import { API_BASE_URL } from '../config/api';

/**
 * Stripe Connect Service
 * Handles all Connect-related API calls for onboarding, products, and checkout
 */

export interface ConnectedAccountResponse {
  accountId: string;
  chargesEnabled: boolean;
}

export interface AccountStatusResponse {
  accountId: string;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  requirementsCurrentlyDue: string[];
  requirementsEventuallyDue: string[];
  identityVerificationStatus: string | null;
  identityVerificationDisabledReason: string | null;
  identityVerificationFailed: boolean;
}

export interface AccountLinkResponse {
  url: string;
  expiresAt: number;
}

export interface LoginLinkResponse {
  url: string;
}

export interface AccountSessionResponse {
  clientSecret: string;
  expiresAt: number;
}

export interface ProductResponse {
  productId: string;
  priceId: string;
  name: string;
  amount: number;
  currency: string;
}

export interface ConnectProduct {
  id: string;
  name: string;
  description: string | null;
  active: boolean;
  prices: Array<{
    id: string;
    currency: string;
    unit_amount: number;
    active: boolean;
  }>;
  metadata: Record<string, string>;
}

export interface CheckoutSessionResponse {
  sessionId: string;
  url: string | null;
  totalAmount: number;
}

/**
 * Get the authentication token from Firebase
 * @returns The Firebase ID token or empty string
 */
const getAuthToken = async (): Promise<string> => {
  if (auth.currentUser) {
    try {
      return await auth.currentUser.getIdToken();
    } catch (error) {
      console.error('Error getting Firebase ID token:', error);
      return '';
    }
  }
  return '';
};

/**
 * Get axios config with authentication header
 */
const getAuthConfig = async () => {
  const token = await getAuthToken();
  return {
    headers: {
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
    },
  };
};

/**
 * Create a Stripe Connect account for an academy
 * @param academyId - The academy ID to link the account to
 * @param email - Email address for the connected account
 * @param businessName - Name of the business/academy
 * @param country - Optional country code (AU or US). If not provided, will be auto-detected from user's region
 * @returns The created account details
 */
export const createConnectedAccount = async (
  academyId: string,
  email: string,
  businessName: string,
  country?: string
): Promise<ConnectedAccountResponse> => {
  const config = await getAuthConfig();
  
  // Get user's region from localStorage or detect it
  let detectedCountry = country;
  if (!detectedCountry) {
    const { regionService } = await import('./regionService');
    try {
      const region = await regionService.detectRegion();
      detectedCountry = region; // Region is 'US' | 'AU', which matches country codes
    } catch (error) {
      console.warn('Failed to detect region, defaulting to AU:', error);
      detectedCountry = 'AU'; // Default to Australia
    }
  }
  
  const response = await axios.post(
    `${API_BASE_URL}/connect/accounts`,
    { academyId, email, businessName, country: detectedCountry },
    config
  );
  return response.data.data;
};

export const createConnectedAccountForOrganization = async (
  organizationId: string,
  email: string,
  businessName: string,
  country?: string
): Promise<ConnectedAccountResponse> => {
  const config = await getAuthConfig();

  let detectedCountry = country;
  if (!detectedCountry) {
    const { regionService } = await import('./regionService');
    try {
      const region = await regionService.detectRegion();
      detectedCountry = region;
    } catch (error) {
      console.warn('Failed to detect region, defaulting to AU:', error);
      detectedCountry = 'AU';
    }
  }

  const response = await axios.post(
    `${API_BASE_URL}/connect/accounts`,
    { organizationId, email, businessName, country: detectedCountry },
    config
  );
  return response.data.data;
};

/**
 * Get the status of a Stripe Connect account
 * Fetches fresh data from Stripe API
 * @param accountId - The Stripe Connect account ID
 * @returns The account status details
 */
export const getAccountStatus = async (accountId: string): Promise<AccountStatusResponse> => {
  const config = await getAuthConfig();
  const response = await axios.get(
    `${API_BASE_URL}/connect/accounts/${accountId}`,
    config
  );
  return response.data.data;
};

/**
 * Create an Account Link for onboarding
 * @param accountId - The Stripe Connect account ID
 * @param refreshUrl - URL to redirect if the link expires
 * @param returnUrl - URL to redirect after onboarding completes
 * @returns The account link URL
 */
export const createAccountLink = async (
  accountId: string,
  refreshUrl: string,
  returnUrl: string
): Promise<AccountLinkResponse> => {
  const config = await getAuthConfig();
  const response = await axios.post(
    `${API_BASE_URL}/connect/account-links`,
    { accountId, refreshUrl, returnUrl },
    config
  );
  return response.data.data;
};

/**
 * Create a Login Link for accessing the Stripe Dashboard
 * @param accountId - The Stripe Connect account ID
 * @returns The login link URL
 */
export const createLoginLink = async (accountId: string): Promise<LoginLinkResponse> => {
  const config = await getAuthConfig();
  const response = await axios.post(
    `${API_BASE_URL}/connect/login-links`,
    { accountId },
    config
  );
  return response.data.data;
};

/**
 * Create an Account Session for embedded components
 * For Express accounts, Stripe handles authentication directly
 * @param accountId - The Stripe Connect account ID
 * @param components - Components configuration
 * @returns The account session with client secret
 */
export const createAccountSession = async (
  accountId: string,
  components: {
    payments?: { enabled: boolean; features?: Record<string, unknown> };
    payouts?: { enabled: boolean; features?: Record<string, unknown> };
    account_onboarding?: { enabled: boolean; features?: Record<string, unknown> };
    tax_settings?: { enabled: boolean; features?: Record<string, unknown> };
    tax_registrations?: { enabled: boolean; features?: Record<string, unknown> };
    payment_details?: { enabled: boolean; features?: Record<string, unknown> };
    [key: string]: { enabled: boolean; features?: Record<string, unknown> } | undefined;
  }
): Promise<AccountSessionResponse> => {
  const config = await getAuthConfig();
  const response = await axios.post(
    `${API_BASE_URL}/connect/account-sessions`,
    { accountId, components },
    config
  );
  return response.data.data;
};

export interface TaxStatusRefreshResponse {
  stripeTaxSettingsStatus?: string | null;
  stripeTaxRegistrationStatus?: string | null;
  stripeTaxCountry?: string | null;
  taxPriceBehavior?: string | null;
}

export const refreshTaxStatus = async (params: {
  academyId?: string;
  organizationId?: string;
}): Promise<TaxStatusRefreshResponse> => {
  const config = await getAuthConfig();
  const response = await axios.post(`${API_BASE_URL}/connect/tax/refresh-status`, params, config);
  return response.data.data;
};

export const updateTaxPriceBehavior = async (
  params: { academyId?: string; organizationId?: string },
  taxPriceBehavior: 'exclusive' | 'inclusive'
): Promise<{ taxPriceBehavior: string }> => {
  const config = await getAuthConfig();
  const response = await axios.post(
    `${API_BASE_URL}/connect/tax/price-behavior`,
    { ...params, taxPriceBehavior },
    config
  );
  return response.data.data;
};

/**
 * Create a Stripe product for a lesson on a connected account
 * @param lessonId - The lesson ID to create a product for
 * @param accountId - The Stripe Connect account ID
 * @returns The created product details
 */
export const createConnectProduct = async (
  lessonId: string,
  accountId: string
): Promise<ProductResponse> => {
  const config = await getAuthConfig();
  const response = await axios.post(
    `${API_BASE_URL}/connect/products`,
    { lessonId, accountId },
    config
  );
  return response.data.data;
};

/**
 * List all products for a connected account
 * Public endpoint - no authentication required
 * @param accountId - The Stripe Connect account ID
 * @param limit - Maximum number of products to return
 * @returns Array of products with their prices
 */
export const listConnectProducts = async (
  accountId: string,
  limit: number = 100
): Promise<ConnectProduct[]> => {
  const response = await axios.get(
    `${API_BASE_URL}/connect/products/${accountId}`,
    { params: { limit } }
  );
  return response.data.data;
};

/**
 * Create a Stripe Checkout session for purchasing a product
 * Public endpoint - no authentication required
 * @param priceId - The Stripe price ID from the connected account
 * @param quantity - Quantity to purchase
 * @param accountId - The Stripe Connect account ID
 * @param successUrl - URL to redirect on successful payment
 * @param cancelUrl - URL to redirect on cancelled payment
 * @param metadata - Optional metadata for the checkout session
 * @returns The checkout session details including URL
 */
export const createCheckoutSession = async (
  priceId: string,
  quantity: number,
  accountId: string,
  successUrl: string,
  cancelUrl: string,
  metadata?: Record<string, string>,
  colorScheme?: 'light' | 'dark'
): Promise<CheckoutSessionResponse> => {
  const response = await axios.post(
    `${API_BASE_URL}/connect/checkout-sessions`,
    {
      priceId,
      quantity,
      accountId,
      successUrl,
      cancelUrl,
      metadata,
      ...(colorScheme ? { colorScheme } : {}),
    }
  );
  return response.data.data;
};

/**
 * Handle checkout success
 * @param sessionId - The Stripe checkout session ID
 * @returns The session details
 */
export const handleCheckoutSuccess = async (sessionId: string): Promise<{ sessionId: string }> => {
  const response = await axios.get(
    `${API_BASE_URL}/connect/checkout-success`,
    { params: { session_id: sessionId } }
  );
  return response.data.data;
};

export default {
  createConnectedAccount,
  getAccountStatus,
  createAccountLink,
  createLoginLink,
  createAccountSession,
  createConnectProduct,
  listConnectProducts,
  createCheckoutSession,
  handleCheckoutSuccess,
};

